'use client';

import { useState, useEffect } from 'react';
import { TrendingUp, Target, Award, Zap, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import Icon from '@/components/ui/AppIcon';


interface Stats {
  totalUnits: number;
  wins: number;
  losses: number;
  pushes: number;
  totalPicks: number;
}

export default function HeroStats() {
  const [stats, setStats] = useState<Stats>({ totalUnits: 0, wins: 0, losses: 0, pushes: 0, totalPicks: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const { data, error } = await supabase
          .from('picks')
          .select('result, units, units_result')
          .neq('result', 'pending');

        if (error) throw error;

        const picks = data || [];
        const wins = picks.filter((p) => p.result === 'win').length;
        const losses = picks.filter((p) => p.result === 'loss').length;
        const pushes = picks.filter((p) => p.result === 'push').length;
        const totalUnits = picks.reduce((a, p) => a + (p.units_result || 0), 0);

        setStats({ totalUnits, wins, losses, pushes, totalPicks: picks.length });
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  const winPct = stats.wins + stats.losses > 0
    ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)
    : '0.0';

  const totalRisked = stats.wins + stats.losses + stats.pushes;
  const roi = totalRisked > 0 ? ((stats.totalUnits / totalRisked) * 100).toFixed(1) : '0.0';

  const statItems = [
    {
      label: 'All-Time Units',
      value: loading ? '—' : `${stats.totalUnits >= 0 ? '+' : ''}${stats.totalUnits.toFixed(1)}`,
      sub: 'net units won',
      icon: TrendingUp,
      color: 'text-[hsl(var(--primary))]',
      bg: 'bg-[hsl(var(--primary)/0.08)]',
      border: 'border-[hsl(var(--primary)/0.2)]',
    },
    {
      label: 'Overall Record',
      value: loading ? '—' : `${stats.wins}-${stats.losses}-${stats.pushes}`,
      sub: `${stats.totalPicks} total picks`,
      icon: Target,
      color: 'text-blue-400',
      bg: 'bg-blue-500/08',
      border: 'border-blue-500/20',
    },
    {
      label: 'Win Rate',
      value: loading ? '—' : `${winPct}%`,
      sub: 'picks graded',
      icon: Award,
      color: 'text-amber-400',
      bg: 'bg-amber-500/08',
      border: 'border-amber-500/20',
    },
    {
      label: 'ROI',
      value: loading ? '—' : `${parseFloat(roi) >= 0 ? '+' : ''}${roi}%`,
      sub: 'return on investment',
      icon: Zap,
      color: 'text-purple-400',
      bg: 'bg-purple-500/08',
      border: 'border-purple-500/20',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {statItems.map((stat) => {
        const Icon = stat.icon;
        return (
          <div key={stat.label} className={`stat-card flex flex-col gap-3 ${stat.bg} border ${stat.border}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium tracking-wide uppercase text-[hsl(var(--foreground-muted))]">{stat.label}</span>
              <div className={`w-8 h-8 rounded-lg ${stat.bg} border ${stat.border} flex items-center justify-center`}>
                <Icon size={15} className={stat.color} />
              </div>
            </div>
            <div className="flex flex-col gap-0.5">
              {loading ? (
                <Loader2 size={18} className="animate-spin text-[hsl(var(--foreground-muted))]" />
              ) : (
                <span className={`font-mono text-2xl lg:text-3xl font-bold tabular-nums ${stat.color}`}>{stat.value}</span>
              )}
              <span className="text-xs text-[hsl(var(--foreground-muted))]">{stat.sub}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}